/***
 * Service qui gère l'état global du tooltip.
 * Un seul tooltip est rendu dans le DOM à la fois (via GesTip.vue).
 * 
 * Utilisation :
 *    - Utilisez la directive `v-ges-tip` pour assigner un tooltip à un élément.
 *      Cette nouvelle directive remplace celle de bootstrap v-tooltip, data-bs-attributes...
 */ 

import {reactive, computed, nextTick} from 'vue';

/**
 * Valeurs par défaut du tooltip.
 * Centralise toutes les valeurs initiales — modifier ici pour changer le comportement global.
 * Utilisé pour initialiser tooltipState et réinitialiser l'état après fermeture.
 */
export const _GESTIP_DEFAULT = {
    visible:            false,  // Visibilité du tooltip
    contentResolver:    null,   // Fonction qui retourne le contenu courant
    html:               false,  // Si true, le contenu est rendu en HTML brut (v-html)
    x:                  0,      // Position left finale (px, fixed)
    y:                  0,      // Position top finale (px, fixed)
    placement:          'top',  // Placement résolu après détection de débordement : 'top' | 'bottom' | 'left' | 'right'
    offset:             10,     // Distance (px) entre le tooltip et l'élément déclencheur
    offsetX:            0,      // Décalage horizontal supplémentaire (px)
    offsetY:            0,      // Décalage vertical supplémentaire (px)
    maxWidth:           300,    // Largeur maximale du tooltip (px)
    target:             null,   // L'élément DOM qui a déclenché le tooltip
    measuring:          false,  // True pendant la phase de mesure — masque le tooltip via la class .measuring
    arrowX:             0,      // Position horizontale de la flèche (px) relative à la boîte du tooltip
    arrowY:             0,      // Position verticale de la flèche (px) relative à la boîte du tooltip
    classes:            []      // Classes CSS supplémentaires appliquées au tooltip 
};

/**
 * État réactif global du tooltip, consommé par le GesTip.vue
 * Ne pas muter directement — utiliser showTooltip() / hideTooltip() / forceHideTooltip()
 */
export const tooltipState = reactive({..._GESTIP_DEFAULT});

/**
 * Computed qui résout le contenu courant du tooltip.
 * contentResolver est toujours une fonction pour que le contenu puisse être réactif
 * (ex: un getter qui lit l'état d'un composant ou d'un store, etc).
 */
export const tooltipContent = computed(() => {
    if (!tooltipState.contentResolver) { return ''; }

    const value = tooltipState.contentResolver();

    return value ?? '';
})

/**
 * Tracker interne d'intention de survol.
 * Empêche le tooltip de se fermer quand le curseur pass de l'élément déclencheur
 * vers l'intérieur du tooltip.
 */
const intent = {
    hoveringTrigger: false,
    hoveringTooltip: false,
    activeTarget:    null
};

const padding       = 8; // Marge minimal (px) entre le tooltip et les bords du viewport
const ARROW_SIZE    = 8; // px — doit correspondre aux dimensions (width/height) de .tooltip-arrow dans le CSS

let hideTimer       = null; // Timer de debounce pour hideTooltip()
let resizeObserver  = null; // ResizeObserver pour recalculer la position si le contenu change de taille

/**
 * Calcule la meilleure position x/y et les offsets de la flèche pour le tooltip.
 * 
 * Fonctionnement :
 *  1. Essaie le placement préféré en premier.
 *  2. Parcourt [top, bottom, left, right] jusqu'à trouver un placement qui tient dans le viewport.
 *  3. Clamp la position du tooltip dans le viewport (marge de `padding` px).
 *  4. Corrige la position de la flèche pour qu'elle reste centrée sur le déclencheur
 *     même quand la boîte du tooltip est clampée au bord du viewport.
 * 
 * @param {HTMLElement} el        - L'élément déclencheur
 * @param {HTMLElement} tooltipEl - L'élément DOM du tooltip (déjà rendu, pour mesurer sa taille)
 * @param {string}      preferred - Placement préféré du tooltip : 'top' | 'bottom' | 'left' | 'right'
 * @param {number}      offset    - Écart entre le déclencheur et le tooltip (px)
 * @param {number}      offsetX   - Décalage horizontal supplémentaire (px)
 * @param {number}      offsetY   - Décalage vertical supplémentaire (px)
 * @returns {{x, y, placement, arrowX, arrowY}}
 */
function calculatePosition(
    el, 
    tooltipEl, 
    preferred, 
    offset = _GESTIP_DEFAULT.offset, 
    offsetX = _GESTIP_DEFAULT.offsetX, 
    offsetY = _GESTIP_DEFAULT.offsetY
) {
    const rect = el.getBoundingClientRect();
    const tRect = tooltipEl.getBoundingClientRect();

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const tw = tRect.width;
    const th = tRect.height;

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Calcule les positions candidates et les offsets de la flèche pour les quatre placements.
    const placements = {
        top: {
            x: centerX - tw / 2 + offsetX,
            y: rect.top - th - offset + offsetY,
            arrowX: tw / 2 - ARROW_SIZE / 2 - offsetX,
            arrowY: th - ARROW_SIZE / 2     // Flèche en bas du tooltip
        },
        bottom: {
            x: centerX - tw / 2 + offsetX,
            y: rect.bottom + offset + offsetY,
            arrowX: tw / 2 - ARROW_SIZE / 2 - offsetX,
            arrowY: -ARROW_SIZE / 2         // Flèche en haut du tooltip
        },
        left: {
            x: rect.left - tw - offset + offsetX,
            y: centerY - th / 2 + offsetY,
            arrowX: tw - ARROW_SIZE / 2,    // Flèche sur la droite du tooltip
            arrowY: th / 2 - ARROW_SIZE / 2 - offsetY
        },
        right: {
            x: rect.right + offset + offsetX,
            y: centerY - th / 2 + offsetY,
            arrowX: -ARROW_SIZE / 2,        // Flèche sur la gauche du tooltip
            arrowY: th / 2 - ARROW_SIZE / 2 - offsetY
        }
    };

    // Retourne true si le tooltip tient entièrement dans le viewport avec la marge `padding`.
    const fits = (x, y) =>
        x >= padding &&
        y >= padding &&
        x + tw <= vw - padding &&
        y + th <= vh - padding;

    // Essaie le placement préféré, puis l'ordre de fallback.
    const order = [preferred, 'top', 'bottom', 'right', 'left'];

    let best = preferred;

    for (const p of order) {
        const pos = placements[p];
        if (fits(pos.x, pos.y)) {
            best = p;
            break;
        }
    }

    const final = placements[best];

    // Clamp la position finale dans le viewport.
    const x = Math.min(Math.max(final.x, padding), vw - tw - padding);
    const y = Math.min(Math.max(final.y, padding), vh - th - padding);

    let arrowX = final.arrowX;
    let arrowY = final.arrowY;

    // Quand le tooltip est clampé horizontalement (top/bottom), déplace la flèche
    // dans la direction opposée pour qu'elle continue de pointer vers le déclencheur.
    if (best === 'top' || best === 'bottom') {
        const overflowX = Math.min(Math.max(x, padding), vw - tw - padding) - x;

        arrowX -= overflowX;
        // Garde la flèche à au moins 12px de chaque bord du tooltip.
        arrowX = Math.max(12, Math.min(tw - 12, arrowX));
    }
    // Même correction pour le débordement vertical sur les placements (left/right).
    if (best === 'left' || best === 'right') {
        const overflowY = Math.min(Math.max(y, padding), vh - th - padding) - y;

        arrowY -= overflowY;
        arrowY = Math.max(12, Math.min(th - 12, arrowY));
    }

    return { x, y, placement: best, arrowX, arrowY };
}

/**
 * Recalcule et applique la position du tooltip à partir de l'état courant.
 * Appelé par le ResizeObserver et après le premier rendu.
 * 
 * @param {HTMLElement} target    - L'élément du déclencheur
 * @param {HTMLElement} tooltipEl - L'élément DOM du tooltip
 */
function updatePosition(target, tooltipEl) {
    if (!target || !tooltipEl) { return; }

    const pos = calculatePosition(
        target,
        tooltipEl,
        tooltipState.placement,
        tooltipState.offset,
        tooltipState.offsetX,
        tooltipState.offsetY
    );

    tooltipState.x = pos.x;
    tooltipState.y = pos.y;

    tooltipState.placement = pos.placement;

    tooltipState.arrowX = pos.arrowX;
    tooltipState.arrowY = pos.arrowY;
}

/**
 * Affiche le tooltip pour l'élément cible donné.
 * 
 * La fonction attend intentionnellement un tick Vue + deux animation frames après avoir
 * rendu le tooltip visible, afin que le navigateur ait le temps de le peindre et de
 * calculer ses dimensions avant que calculatePosition() ne les lise.
 * 
 * Un ResizeObserver est ensuite attaché au tooltip pour recalculer sa position si
 * son contenu change de taille (ex: texte dynamique plus long).
 * 
 * @param {Object} options
 *      @param {HTMLElement}      options.target        - L'élément déclencheur
 *      @param {string|function}  options.content       - Contenu texte, ou une fonction qui le retourne
 *      @param {boolean}         [options.html]         - Transforme le contenu en HTML
 *      @param {string}          [options.placement]    - Placement préféré
 *      @param {number}          [options.offset]       - Écart entre déclencheur et tooltip en px
 *      @param {number}          [options.offsetX]      - Décalage horizontal supplémentaire en px
 *      @param {number}          [options.offsetY]      - Décalage vertical supplémentaire en px
 *      @param {number}          [options.maxWidth]     - Largeur max du tooltip en px
 *      @param {string|string[]} [options.classes]      - Classe(s) CSS supplémentaire(s) sur le tooltip
 * @returns 
 */
export async function showTooltip({
    target,
    content,
    html        = _GESTIP_DEFAULT.html,
    placement   = _GESTIP_DEFAULT.placement,
    offset      = _GESTIP_DEFAULT.offset,
    offsetX     = _GESTIP_DEFAULT.offsetX,
    offsetY     = _GESTIP_DEFAULT.offsetY,
    maxWidth    = _GESTIP_DEFAULT.maxWidth,
    classes     = _GESTIP_DEFAULT.classes
}) {
    // Réinitialise la position de la flèche avant la mesure pour éviter un flash de l'ancienne valeur.
    tooltipState.arrowX = _GESTIP_DEFAULT.arrowX;
    tooltipState.arrowY = _GESTIP_DEFAULT.arrowY;

    if (!target) { return; }

    intent.hoveringTrigger  = true;
    intent.activeTarget     = target;

    clearTimeout(hideTimer);

    // Rend le tooltip visible, mais masqué (.measuring) pour pouvoir lire ses dimensions.
    tooltipState.visible        = true;
    tooltipState.measuring      = true;
    tooltipState.html           = html;
    tooltipState.placement      = placement;
    tooltipState.contentResolver = 
        typeof content === 'function' ? content : () => content;
    tooltipState.maxWidth   = maxWidth;
    tooltipState.offset     = offset;
    tooltipState.offsetX    = offsetX;
    tooltipState.offsetY    = offsetY;
    tooltipState.classes    = Array.isArray(classes) ? classes : [classes];

    // Un tick Vue pour que le tooltip soit rendu, puis deux animations frames
    // pour que le navigateur ait calculé le layout et les dimensions.
    await nextTick();
    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve => requestAnimationFrame(resolve));

    const tooltipEl = document.querySelector('.ges-tooltip');
    if (!tooltipEl) { return; }

    updatePosition(target, tooltipEl);
    tooltipState.measuring = false;

    // Attache un ResizeObserver pour repositionner le tooltip si son contenu
    // change de taille après l'affichage (ex: contenu dynamique).
    resizeObserver?.disconnect();
    resizeObserver = new ResizeObserver(() => {
        if (!tooltipState.visible) { return; }
        updatePosition(target, tooltipEl);
    });
    resizeObserver.observe(tooltipEl);

    // Un dernier recaulcul après le prochain paint pour corriger d'éventuels
    // décalages liés au rendu async.
    requestAnimationFrame(() => {
        if (!tooltipState.visible) { return; }
        updatePosition(target, tooltipEl);
    });
}

/**
 * Masque le tooltip après un court délai.
 * Le délai permet au curseur de se déplacer du déclencheur vers le tooltip sans que celui-ci disparaisse.
 * Le tooltip ne se fermera PAS si le curseur est actuellement à l'intérieur
 * 
 * @param {number} delay - Délai en ms avant la fermeture. Passer 0 pour une fermeture immédiate
 */
export function hideTooltip(delay = 100) {
    intent.hoveringTrigger = false;

    clearTimeout(hideTimer);

    hideTimer = setTimeout(() => {
        if (!intent.hoveringTrigger && !intent.hoveringTooltip) {
            resizeObserver?.disconnect();
            resizeObserver = null;

            Object.assign(tooltipState, _GESTIP_DEFAULT);
        }
    }, delay);
}

/**
 * Masque le tooltip immédiatement, en ignorant le délai de l'état de survol.
 * À utiliser quand l'élément déclencheur est supprimé, désactivé ou lors d'une navigation.
 * 
 * ex: est utilisé dans router/index.js dans le afterEach guard
 */
export function forceHideTooltip() {
    clearTimeout(hideTimer);

    resizeObserver?.disconnect();
    resizeObserver = null;

    Object.assign(tooltipState, _GESTIP_DEFAULT);

    intent.hoveringTrigger = false;
    intent.hoveringTooltip = false;
    intent.activeTarget = null;
}

/**
 * Notifie le service que le curseur est entré ou sorti de la boîte du tooltip.
 * Appelé par GesTip.vue sur mouseenter / mouseleave.
 * Tant que hoveringTooltip est true, le timer de hideTooltip() ne fermera pas le tooltip.
 * 
 * @param {boolean} state - true à l'entrée, false à la sortie
 */
export function setTooltipHover(state) {
    intent.hoveringTooltip = state;
}